import { useMemo } from 'react'

import type { SeriesFlagsOptions } from 'highcharts'
import type { IAppEvent, IFormattedEvent } from '~/types/event'
import type {
  ExtendedHighchartsOptions,
  GraphConfig,
  IBuilderConfig,
  IGetOverallResponse,
  IGetSeriesDataBody,
  IGetSeriesDataResponse,
} from '~/types/graph-builder'

import { getGraphOptions } from './graph-options'

import { DEFAULT_DAI_MULTIPLE_VALUES, DEFAULT_DAI_SINGLE_VALUE } from '~/const'
import { useAnalyticsContext } from '~/providers/Analytics'
import { APP_EVENTS } from '~/types/event'
import { Mode } from '~/types/graph-builder'
import { isBusinessReportsFeature } from '~/utils/analytics-context'
import dayjs from '~/utils/dayjs'
import { handleAxisOverrides, handleLegend, handleTooltip, populateSeries } from '~/utils/graphs-builder'

const startOfTheDayTs = (ts: number) => dayjs.utc(ts).startOf('day').valueOf()

export const useFormattedBody = ({
  appKey,
  config,
  deviceType,
  kpiField,
  withDefaultInstallationCountry,
}: {
  appKey: string
  config: IBuilderConfig
  deviceType: string
  kpiField: string
  withDefaultInstallationCountry?: boolean
}): IGetSeriesDataBody[] => {
  const { state } = useAnalyticsContext()

  const isBusinessReportGraph = useMemo(() => isBusinessReportsFeature(state.feature), [state.feature])

  const body = useMemo(() => {
    const maxEnd = new Date()
    const nbDaysToIgnore: number = +config.nbLastDaysToIgnore
    const kpiFieldToUse = config.forceKPIField ? config.forceKPIField : kpiField

    let end = new Date(state.computedFiltersDate.range.end)

    maxEnd.setDate(maxEnd.getDate() - (nbDaysToIgnore + 1))

    if (maxEnd.getTime() < end.getTime()) {
      end = maxEnd
    }

    const breakdowns: string[] = []

    if (state.filters.breakdown && state.filters.breakdown.length > 0) {
      breakdowns.push(...state.filters.breakdown)
    } else {
      config.series?.forEach(({ defaultBreakdown }) => {
        if (defaultBreakdown) {
          breakdowns.push(defaultBreakdown)
        }
      })
    }

    // if breakdown is dsv3 -> replace by navigation_graph_id
    // if breakdown is abtest -> replace by abtest_value
    // because it's not real breakdowns, it's a way to display additional input fields
    breakdowns.forEach((breakdown, index) => {
      if (breakdown === 'dsv3') {
        if (!state.filters.placementKey?.length) {
          breakdowns.splice(index)
        } else {
          // remove breakdown if no placement key
          breakdowns[index] = 'navigation_graph_id'
        }
      }
      if (breakdown === 'abtest') {
        if (!state.filters.abtestKey?.length) {
          // remove breakdown if no abtest key
          breakdowns.splice(index)
        } else {
          breakdowns[index] = 'abtest_value'
        }
      }
    })

    const batchedPayload =
      config.series?.map((el) => {
        const singlePayload = {
          app_key: isBusinessReportGraph ? '' : appKey,
          breakdowns: isBusinessReportGraph && config.mode !== Mode.OVERALL ? [...breakdowns, 'date'] : breakdowns,
          device_type: isBusinessReportGraph ? '' : deviceType,
          feature_name: config.featureName,
          filters: [
            {
              condition_type: 'gt',
              key: isBusinessReportGraph ? 'date' : 'installation_date',
              values: [dayjs(state.computedFiltersDate.range.start).tz('Europe/Paris').format('YYYY-MM-DD')],
            },
            {
              condition_type: 'lt',
              key: isBusinessReportGraph ? 'date' : 'installation_date',
              values: [dayjs(end).tz('Europe/Paris').format('YYYY-MM-DD')],
            },
          ],
          kpi_values: {
            field: isBusinessReportGraph ? 'date' : kpiFieldToUse,
            normalize_by: el.normalizedBy,
            values: el.values,
            with_inf: !!el.withInf && kpiFieldToUse !== 'dai',
            with_sup: !!el.withSup && kpiFieldToUse !== 'dai',
            with_trend: !!el.withTrend && kpiFieldToUse !== 'dai',
          },
        }

        const minNbu = el.minNbu

        if (!isBusinessReportGraph && minNbu && minNbu > 0) {
          singlePayload.filters.push({
            condition_type: 'range',
            key: 'nbu',
            values: [minNbu.toString()],
          })
        }

        state.computedFilters.forEach((filter) => {
          let values = [...filter.value]

          switch (filter.key) {
            case 'breakdown':
            case 'kpi':
              return
            case 'placement_key':
            case 'navigation_graph_id':
              // Send these filters only if the breakdown is dsv3
              if (!singlePayload.breakdowns.includes('navigation_graph_id')) {
                return
              }
              break
            case 'abtest_key':
              // Send these filters only if the breakdown is abtest
              if (!singlePayload.breakdowns.includes('abtest_value')) {
                return
              }
              break
            case 'installation_country':
              if (isBusinessReportGraph) {
                return
              }

              values = withDefaultInstallationCountry ? values.filter((el) => el !== 'WW') : []
              break
            case 'country':
              if (!isBusinessReportGraph) {
                return
              }
              break
            case 'dai':
              if (isBusinessReportGraph) {
                return
              }

              if (kpiFieldToUse === kpiField) {
                break
              }

              switch (kpiFieldToUse) {
                case 'dai':
                  values = DEFAULT_DAI_MULTIPLE_VALUES
                  break
                case 'installation_date':
                  values = DEFAULT_DAI_SINGLE_VALUE
                  break
              }
          }

          if (values?.length == 0) {
            return
          }

          singlePayload.filters.push({
            condition_type: 'in',
            key: filter.key,
            values:
              kpiFieldToUse === 'dai'
                ? values.sort((a, b) => {
                    if (a.length !== b.length) {
                      return a.length - b.length
                    }
                    return a.localeCompare(b)
                  })
                : values,
          })
        })

        return singlePayload
      }) || []

    return batchedPayload
  }, [appKey, config, deviceType, isBusinessReportGraph, kpiField, state, withDefaultInstallationCountry])

  return body
}

export const useFormattedEventsSeries = ({
  config,
  eventsData,
  series,
}: {
  config: IBuilderConfig
  eventsData?: IAppEvent[]
  series?: IGetSeriesDataResponse[]
}): SeriesFlagsOptions | undefined => {
  const { state } = useAnalyticsContext()

  const dates =
    series?.[0]?.data
      ?.map(({ data }) => data)
      .flat()
      .map((el) => el[0]) || []

  const firstDataDate = Math.min(...dates)
  const lastDataDate = Math.max(...dates)

  const appEventsData: IFormattedEvent[] = useMemo(() => {
    const kpiFieldToUse = config.forceKPIField ? [config.forceKPIField] : state.filters.kpi

    return kpiFieldToUse?.includes('dai')
      ? []
      : eventsData
          ?.filter(({ date }) => {
            const eventDateTimestamp = new Date(date).getTime()
            return eventDateTimestamp >= firstDataDate && eventDateTimestamp <= lastDataDate
          })
          .map(({ date, event_type, value }) => {
            const { backgroundColor, fontColor } = APP_EVENTS[event_type] || {}

            return {
              color: '#1d1d1d',
              eventType: event_type,
              fillColor: backgroundColor,
              style: {
                color: fontColor,
              },
              text: value,
              title: value,
              x: new Date(date).getTime(),
            }
          }) || []
  }, [config.forceKPIField, eventsData, firstDataDate, lastDataDate, state.filters.kpi])

  const appEventsOnAxis: SeriesFlagsOptions | undefined = useMemo(
    () =>
      appEventsData.length > 0
        ? {
            data: appEventsData.sort((a, b) => {
              if (a.x > b.x) return 1
              if (a.x < b.x) return -1
              return 0
            }),
            includeInDataExport: false,
            name: 'Events',
            shape: 'squarepin',
            showInLegend: false,
            style: {
              fontFamily: 'ModernGothic',
              fontSize: '11px',
              fontWeight: '400',
            },
            tooltip: {
              footerFormat: '</div>',
              headerFormat: '<div><div>{point.key}</div>',
              pointFormatter: function (this) {
                // @ts-expect-error text does not exist on type Point
                if (this.text) {
                  // @ts-expect-error text does not exist on type Point
                  return `<div><b>${this.text}</b></div>`
                }
                return ''
              },
              xDateFormat: '%b %d, %Y',
            },
            type: 'flags',
          }
        : undefined,
    [appEventsData]
  )

  return appEventsOnAxis
}

export const useOptions = ({
  appEventsSeries,
  config,
  overall = [],
  series,
}: {
  appEventsSeries?: SeriesFlagsOptions
  config: IBuilderConfig
  overall?: IGetOverallResponse[]
  series?: IGetSeriesDataResponse[]
}): ExtendedHighchartsOptions | undefined => {
  const { state } = useAnalyticsContext()

  if (series?.some(({ data }) => !!data)) {
    const withOverallData = !!(overall && overall?.[0]?.data)
    const kpiFieldToUse = config.forceKPIField ? [config.forceKPIField] : state.filters.kpi
    const tmpOptions = getGraphOptions(config.title, config.series?.[0].graphType, withOverallData)

    const formattedConfig: GraphConfig = {
      // @ts-expect-error Type '(IOverallSerie | null)[]' is not assignable to type 'IOverallSerie[]'.
      overallData: overall
        .map(({ data }) => data)
        .flat()
        .filter((el) => !!el),
      series:
        series
          ?.map(
            (el, idx) =>
              el.data?.map((subseries) => ({
                ...subseries,
                graphType: config.series[idx].graphType,
                seriesNumber: idx,
                yAxis: config.series[idx].yAxisName,
              }))
          )
          .flat()
          .filter((el) => el !== undefined) || [],
      xAxis: {
        dataType: kpiFieldToUse?.includes('dai') ? 'dai' : 'datetime',
        name: config.title,
      },
      yAxes: config.yAxes,
    }

    const [yAxis, xAxis] = handleAxisOverrides(tmpOptions, formattedConfig)

    tmpOptions.yAxis = yAxis
    tmpOptions.xAxis = xAxis

    tmpOptions.series = populateSeries(formattedConfig)
    tmpOptions.tooltip = handleTooltip(tmpOptions, formattedConfig)
    tmpOptions.legend = handleLegend(tmpOptions, formattedConfig)

    if (appEventsSeries) {
      const timestampReworkedEventSeries = {
        ...appEventsSeries,
        data: appEventsSeries?.data?.map((event) => ({
          ...event,
          x: event.x && startOfTheDayTs(event.x as number),
        })),
      }

      tmpOptions.series.push(timestampReworkedEventSeries)
    }

    return tmpOptions
  }
  return undefined
}
